import { Professor } from './../../../../../core/interfaces/admin/professor';
// src/app/components/admin/theme/theme.component.ts

import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import {
  LangChangeEvent,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { Subject as RxJsSubject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { CustomTabComponent } from '../../../../ui/custom-tab/custom-tab.component';
import { ButtonComponent } from '../../../../ui/button/button.component';
import { SyncfusionL10nService } from '../../../../../shared/utils/syncfusion-l10n.service';
import { LanguageService } from '../../../../../core/services/utils/language.service';
import { SwalService } from '../../../../../core/services/utils/swal.service';
import { ModalComponent } from '../../../../ui/modal/modal.component';
import { AddThemeComponent } from '../../../../../shared/modals/admin/add-theme/add-theme.component';
import { LearningService } from '../../../../../core/services/learning-path.service';
import {
  Theme,
  CreateThemeDto,
  UpdateThemeDto,
} from '../../../../../core/interfaces/theme';
import { Subject } from '../../../../../core/interfaces/subject';
import { LearningPath } from '../../../../../core/interfaces/learning-path';
import { AuthService } from '../../../../../core/services/utils/auth.service';
import {
  ProfessorLearningPathLightDto,
  ProfessorSubjectLightDto,
} from '../../../../../core/interfaces/professor';

@Component({
  selector: 'app-teacher-themes',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    CustomTabComponent,
    ButtonComponent,
    ModalComponent,
    AddThemeComponent,
    FormsModule,
  ],
  templateUrl: './themes.component.html',
  styleUrl: './themes.component.css',
})
export class ThemesComponent implements OnInit, OnDestroy {
  private translate = inject(TranslateService);
  private syncfusionL10n = inject(SyncfusionL10nService);
  private languageService = inject(LanguageService);
  private learningService = inject(LearningService);
  private swalService = inject(SwalService);
  private authService = inject(AuthService);
  private destroy$ = new RxJsSubject<void>();

  themes: Theme[] = [];
  subjects: ProfessorSubjectLightDto[] = [];
  learningPaths: ProfessorLearningPathLightDto[] = [];
  selectedPathId: number | null = null;
  selectedSubjectId: number | null = null;
  isRTL = false;
  currentLocale = 'ar';
  isLoading = false;
  isLoadingPaths = false;
  isLoadingSubjects = false;
  isSaving = false;
  error: string | null = null;

  translatedHeaders = {
    title: '',
    description: '',
    subject: '',
    displayOrder: '',
    status: '',
    actions: '',
  };

  themeColumns: Array<{
    field: string;
    headerText: string;
    width: number;
    textAlign?: string;
    template?: string;
    freeze?: string;
  }> = [];

  showThemeModal = false;
  selectedTheme: Theme | null = null;
  isEditMode = false;
  professorId: string | null = null;

  async ngOnInit(): Promise<void> {
    this.initializeComponent();
    this.subscribeToLanguageChanges();
    this.professorId = await this.authService.getUserId();

    if (!this.professorId) {
      console.error('Professor ID not found');
      return;
    }

    this.loadLearningPaths();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeComponent(): void {
    this.currentLocale = this.languageService.getCurrentLang();
    this.isRTL = !this.languageService.isLTR();
    this.loadTranslations();
  }

  private subscribeToLanguageChanges(): void {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: LangChangeEvent) => {
        this.currentLocale = event.lang;
        this.isRTL = !this.languageService.isLTR();
        this.loadTranslations();
      });
  }

  loadTranslations(): void {
    this.translate
      .get([
        'DATA_TABLE.TITLE',
        'DATA_TABLE.DESC',
        'DATA_TABLE.SUBJECT',
        'DATA_TABLE.DISPLAY_ORDER',
        'DATA_TABLE.STATUS',
        'DATA_TABLE.ACTIONS',
      ])
      .pipe(takeUntil(this.destroy$))
      .subscribe((translations) => {
        this.translatedHeaders = {
          title: translations['DATA_TABLE.TITLE'],
          description: translations['DATA_TABLE.DESC'],
          subject: translations['DATA_TABLE.SUBJECT'],
          displayOrder: translations['DATA_TABLE.DISPLAY_ORDER'],
          status: translations['DATA_TABLE.STATUS'],
          actions: translations['DATA_TABLE.ACTIONS'],
        };
        this.buildColumns();
      });
  }

  buildColumns(): void {
    this.themeColumns = [
      {
        field: 'title',
        headerText: this.translatedHeaders.title,
        width: 200,
        textAlign: this.isRTL ? 'Right' : 'Left',
      },
      {
        field: 'description',
        headerText: this.translatedHeaders.description,
        width: 250,
        textAlign: this.isRTL ? 'Right' : 'Left',
      },
      {
        field: 'displayOrder',
        headerText: this.translatedHeaders.displayOrder,
        width: 120,
        textAlign: 'Center',
      },
      {
        field: 'isActive',
        headerText: this.translatedHeaders.status,
        width: 100,
        textAlign: 'Center',
      },
      {
        field: 'id',
        headerText: '',
        width: 170,
        freeze: 'Right',
        textAlign: 'Center',
        template: 'actionTemplate',
      },
    ];
  }

  loadLearningPaths(): void {
    if (!this.professorId) {
      console.error('Professor ID is missing');
      return;
    }

    this.isLoadingPaths = true;

    this.learningService
      .getProfessorLearningPath(this.professorId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoadingPaths = false)),
      )
      .subscribe({
        next: (response) => {
          this.learningPaths = response?.data ?? [];

          if (this.learningPaths.length > 0) {
            this.selectedPathId = this.learningPaths[0].id;
            this.loadSubjects();
          }
        },
        error: (error) => {
          console.error('Error loading learning paths:', error);

          this.translate
            .get('ADMIN.ERROR_LOADING_PATHS')
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => {
              this.swalService.warning(message);
            });
        },
      });
  }

  loadSubjects(): void {
    if (!this.selectedPathId) return;

    this.isLoadingSubjects = true;
    this.subjects = [];
    this.selectedSubjectId = null;
    this.themes = [];

    this.learningService
      .getProfessorSubjects(this.professorId!, this.selectedPathId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoadingSubjects = false)),
      )
      .subscribe({
        next: (response) => {
          this.subjects = response.data;
          if (this.subjects.length > 0) {
            this.selectedSubjectId = this.subjects[0].id;
            this.loadThemes();
          }
        },
        error: (error) => {
          console.error('Error loading subjects:', error);
          this.translate
            .get('ADMIN.ERROR_LOADING_SUBJECTS')
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => {
              this.swalService.warning(message);
            });
        },
      });
  }

  loadThemes(): void {
    if (!this.selectedSubjectId) return;

    this.isLoading = true;
    this.error = null;

    this.learningService
      .getThemesBySubject(this.selectedSubjectId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: (response) => {
          this.themes = response.data;
        },
        error: (error) => {
          console.error('Error loading themes:', error);
          this.translate
            .get('ADMIN.ERROR_LOADING_THEMES')
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => {
              this.error = message;
              this.swalService.warning(message);
            });
        },
      });
  }

  onPathChange(pathId: number): void {
    this.selectedPathId = pathId;
    this.loadSubjects();
  }

  onSubjectChange(subjectId: number): void {
    this.selectedSubjectId = subjectId;
    this.loadThemes();
  }

  handleRowClick(theme: Theme): void {
    console.log('Clicked theme:', theme);
  }

  addTheme(): void {
    if (!this.selectedSubjectId) {
      this.translate
        .get('ADMIN.SELECT_SUBJECT_FIRST')
        .pipe(takeUntil(this.destroy$))
        .subscribe((message) => {
          this.swalService.error(message);
        });
      return;
    }
    this.selectedTheme = null;
    this.isEditMode = false;
    this.showThemeModal = true;
  }

  onEditTheme(theme: Theme): void {
    this.selectedTheme = theme;
    this.isEditMode = true;
    this.showThemeModal = true;
  }

  onDeleteTheme(theme: Theme): void {
    this.translate
      .get('ADMIN.CONFIRM_DELETE_THEME')
      .pipe(takeUntil(this.destroy$))
      .subscribe((message) => {
        const confirmMessage = message.replace('{title}', theme.title);
        this.swalService.confirm(confirmMessage, () => {
          this.deleteTheme(theme.id);
        });
      });
  }

  private deleteTheme(themeId: number): void {
    this.learningService
      .deleteTheme(themeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.translate
            .get('ADMIN.THEME_DELETED_SUCCESS')
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => {
              this.swalService.success(message);
              this.loadThemes();
            });
        },
        error: (error) => {
          console.error('Error deleting theme:', error);
          this.translate
            .get('ADMIN.ERROR_DELETING_THEME')
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => {
              this.swalService.warning(message);
            });
        },
      });
  }

  onShowTheme(theme: Theme): void {
    console.log('Displaying theme:', theme);
  }

  // Replace the handleThemeSaved method in theme.component.ts with this enhanced version:

  handleThemeSaved(themeData: CreateThemeDto | UpdateThemeDto): void {
    if (!this.selectedSubjectId) {
      console.error('No subject selected');
      return;
    }

    console.log('=== Theme Save Debug ===');
    console.log('Selected Subject ID:', this.selectedSubjectId);
    console.log('Received theme data:', themeData);
    console.log('Is Edit Mode:', this.isEditMode);
    console.log('Selected Theme:', this.selectedTheme);

    this.isSaving = true;

    // Check if themeData has an id property to determine if it's an update
    const isUpdate = 'id' in themeData && themeData.id !== undefined;

    console.log('Determined operation:', isUpdate ? 'UPDATE' : 'CREATE');
    console.log('Final DTO being sent:', themeData);

    const operation = isUpdate
      ? this.learningService.updateTheme(
          (themeData as UpdateThemeDto).id,
          themeData as UpdateThemeDto,
        )
      : this.learningService.createTheme(themeData as CreateThemeDto);

    operation
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isSaving = false)),
      )
      .subscribe({
        next: (response) => {
          console.log('Success response:', response);
          const messageKey = isUpdate
            ? 'ADMIN.THEME_UPDATED_SUCCESS'
            : 'ADMIN.THEME_CREATED_SUCCESS';

          this.translate
            .get(messageKey)
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => {
              this.swalService.success(message);
              this.closeModal();
              this.loadThemes();
            });
        },
        error: (error) => {
          console.error('=== Error Details ===');
          console.error('Full error object:', error);
          console.error('Error status:', error.status);
          console.error('Error message:', error.message);
          console.error('Error details:', error.error);

          const errorKey = isUpdate
            ? 'ADMIN.ERROR_UPDATING_THEME'
            : 'ADMIN.ERROR_CREATING_THEME';

          this.translate
            .get(errorKey)
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => {
              this.swalService.warning(message);
            });
        },
      });
  }

  onModalClosed(): void {
    this.closeModal();
  }

  private closeModal(): void {
    this.showThemeModal = false;
    this.selectedTheme = null;
    this.isEditMode = false;
  }

  setGridLanguage(lang: string): void {
    this.currentLocale = lang;
  }
}
